import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { addAlert } from "../../slices/alert";
import { v4 as uuidv4 } from "uuid";
import { generateAlertText } from "../../utils/boilerplate";
import {
  removeFileFromUploads,
  validateAndParsePdf,
  validateAndParseSpreadsheet,
} from "../../slices/uploads";
import { useDropzone } from "react-dropzone";
import Spinner from "../layout/Spinner";
import { CloudArrowUpIcon, TrashIcon } from "@heroicons/react/24/solid";
import getButtonStyle from "../../utils/styles";
import Button from "../layout/Button";
import { translateText } from "../../utils/translation";

interface UploadDocumentDropzoneProps {
  id: string;
  className?: string;
  limit: number;
  page: "newsletters";
  dataType: "spreadsheet" | "pdf";
  acceptedFilesObject: any;
  blockId?: string | null;
  setCurrentFileId: (e: any) => void;
}

export default function UploadDocumentDropzone({
  id,
  className,
  limit,
  page,
  dataType,
  acceptedFilesObject,
  blockId,
  setCurrentFileId,
}: UploadDocumentDropzoneProps) {
  const dispatch = useAppDispatch();

  const {
    spreadsheets: { newsletters: newsletterSpreadsheets },
    pdf: { newsletters: newsletterPdfs },
  } = useAppSelector((state) => state.uploads);
  const { user } = useAppSelector((state) => state.auth);
  const language = user === null ? "EN" : user.language;

  let objects: any = [];
  if (page === "newsletters") {
    if (dataType === "spreadsheet") {
      objects = newsletterSpreadsheets;
    }
    if (dataType === "pdf") {
      objects = newsletterPdfs;
    }
  }

  const objectsData = objects.data;
  const sortedData = Object.fromEntries(
    Object.entries(objectsData).sort(
      (x: any, y: any) => y[1].timestamp - x[1].timestamp,
    ),
  );
  const sortedObjectIds = Object.keys(sortedData);

  // Functions
  function tooManyUploads() {
    dispatch(
      addAlert(generateAlertText("max_uploads_reached", language), "warning"),
    );
  }

  const handleDrop = useCallback((acceptedFiles: any) => {
    for (let i = 0; i < acceptedFiles.length; i += 1) {
      const file = acceptedFiles[i];
      const { name } = file;
      const fileId = uuidv4();
      const reader: any = new FileReader();
      const timestamp = Date.now();
      if (dataType === "spreadsheet") {
        reader.readAsDataURL(file);
        reader.onload = () => {
          const b64Data = reader.result.split("base64,")[1];
          dispatch(
            validateAndParseSpreadsheet({
              fileId,
              name,
              timestamp,
              b64Data,
              extension: file.type.split("/")[1],
              page,
            }),
          );
        };
      } else if (dataType === "pdf") {
        reader.readAsArrayBuffer(file);
        reader.onload = () => {
          const uArray = new Uint8Array(reader.result);
          dispatch(
            validateAndParsePdf(
              fileId,
              name,
              timestamp,
              uArray,
              file.type.split("/")[1],
              page,
            ),
          );
        };
      }
      setCurrentFileId(fileId);
    }
  }, []);

  function dropFile(acceptedFiles: any[], numFiles: number) {
    if (numFiles + acceptedFiles.length > limit) {
      tooManyUploads();
      return;
    }
    handleDrop(acceptedFiles);
  }

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: acceptedFilesObject,
      onDrop: (acceptedFiles) =>
        dropFile(acceptedFiles, Object.keys(objectsData).length),
      maxFiles: limit,
    });

  function addFileToRemoved(e: any, args: any[]) {
    const [fileId] = args;
    dispatch(removeFileFromUploads(fileId, page, dataType, blockId));
  }

  function renderProcessedFile(fileId: string) {
    if (!sortedObjectIds.includes(fileId)) return null;
    const candidateObject = objects.data[fileId];
    if (candidateObject === undefined) return null;
    const { name, loading, success, data } = candidateObject;
    if (
      name === undefined ||
      loading === undefined ||
      success === undefined ||
      data === undefined
    )
      return null;
    if (loading) {
      return (
        <div className="rounded-md card bg-light/20 mx-2 mb-4 p-4" key={fileId}>
          <Spinner size={8} className="mx-auto" padding={2} colour="info" />
          <div className="">
            <p className="text-xs text-secondary italic break-words">
              {name.length <= 25 ? name : name.slice(0, 25) + "..."}
            </p>
          </div>
        </div>
      );
    }
    if (!success) {
      dispatch(
        addAlert(generateAlertText("process_file_error", language), "danger"),
      );
      return null;
    }
    if (data === null) {
      dispatch(
        addAlert(generateAlertText("process_file_error", language), "danger"),
      );
      return null;
    }
    return (
      <div className="flex" key={fileId}>
        <div className="m-auto ml-0">
          <span className="flex text-secondary text-xs my-auto">
            <Button
              type="button"
              className={`${getButtonStyle("solid", "hoverDanger")} mr-2`}
              onClick={addFileToRemoved}
              onClickArgs={[fileId]}
            >
              <TrashIcon className={`h-5 w-5 text-danger`} aria-hidden="true" />
            </Button>
            <span className="m-auto">
              {name.length <= 50 ? name : name.slice(0, 50) + "..."}
            </span>
          </span>
        </div>
      </div>
    );
  }

  // Static data
  let dropzoneBorderClass = "";
  if (isFocused) {
    dropzoneBorderClass = "border-primary";
  }
  if (isDragAccept) {
    dropzoneBorderClass = "border-success";
  }
  if (isDragReject) {
    dropzoneBorderClass = "border-danger";
  }

  return (
    <div className="max-w-xl">
      <div className="grid grid-cols-1" id="form-group-row">
        {/* Dropzone */}
        <div className="container mb-2">
          <div
            {...getRootProps({
              className: `grid grid-cols-1 justify-center text-center md:px-12 px-8 py-4 border-2 border-dashed border-light rounded-lg bg-light/30 ${dropzoneBorderClass}`,
            })}
          >
            <input {...getInputProps()} />
            <div className="row justify-center text-center">
              <CloudArrowUpIcon className="h-6 w-6 text-secondary mx-auto" />
            </div>
            <div className="row">
              <p className="pt-2 text-sm text-secondary">
                {translateText("upload_file", language)}
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1">
          {Object.keys(sortedData).length > 0 &&
            Object.keys(sortedData).map((x: any) => renderProcessedFile(x))}
        </div>
      </div>
    </div>
  );
}

UploadDocumentDropzone.defaultProps = {
  className: "",
  blockId: null,
};
